/**
 * MoJ Q&A Wave 2 — curated deep-dive answers.
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-03)
 * - นักแปลที่ขึ้นทะเบียนกับกระทรวงยุติธรรมรับรองความถูกต้องของคำแปล
 * - นิติกรณ์/รับรองเอกสาร: กองสัญชาติและนิติกรณ์ กรมการกงสุล
 *   กระทรวงการต่างประเทศ 200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน)
 *   — consular.mfa.go.th
 * - ประเทศไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผลกับไทย 28 ก.พ. 2027
 *   (src/data/legal-facts.ts)
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import type { MojQA } from "./questions-500";

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — ระเบียบของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

export const MOJ_QA_WAVE2: MojQA[] = [
  {
    slug: "moj-w2-ใครรับรองคำแปลได้บ้าง",
    q: "คำแปลเอกสารในไทย ใครรับรองได้บ้าง และต่างกันอย่างไร",
    aShort:
      "มีสามชั้นที่พบบ่อย คือ นักแปลที่ขึ้นทะเบียนกับกระทรวงยุติธรรมรับรองความถูกต้องของคำแปล ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) รับรองลายมือชื่อหรือสำเนา และกองสัญชาติและนิติกรณ์ กรมการกงสุล รับรองเอกสารเพื่อใช้ต่างประเทศ",
    aLong:
      "การเลือกชั้นรับรองต้องอ่านจากเช็กลิสต์ของผู้รับเอกสาร ไม่ใช่เลือกจากความสะดวก ถ้าปลายทางเป็นศาลหรือหน่วยงานราชการไทย คำแปลที่รับรองโดยนักแปลมักเพียงพอ ถ้าปลายทางเป็นหน่วยงานต่างประเทศ ส่วนใหญ่กำหนดให้ผ่านนิติกรณ์ของกรมการกงสุลก่อน แล้วจึงประทับตราสถานทูตปลายทางถ้าสถานทูตนั้นกำหนด ค่าธรรมเนียมนิติกรณ์คือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน " +
      TAIL,
    synonyms: ["ใครรับรองคำแปลได้", "รับรองคำแปล ต่างกันอย่างไร", "certified translation thailand who can certify"],
    cluster: "certification",
  },
  {
    slug: "moj-w2-เอกสารต้นฉบับต้องใช้ตัวจริงไหม",
    q: "ส่งแปลต้องใช้ต้นฉบับตัวจริงไหม หรือสแกนก็พอ",
    aShort:
      "ขั้นตอนแปลใช้ไฟล์สแกนคุณภาพสูงได้ แต่ขั้นตอนรับรองต่อที่กรมการกงสุลหรือสถานทูตส่วนใหญ่กำหนดให้แสดงต้นฉบับหรือสำเนาที่หน่วยงานผู้ออกรับรองแล้ว",
    aLong:
      "แนวทางที่ลดการตีกลับคือ เริ่มงานแปลจากไฟล์สแกนเพื่อประหยัดเวลา และเตรียมต้นฉบับหรือสำเนารับรองไว้ให้พร้อมสำหรับวันยื่นรับรอง สแกนต้องอ่านออกทุกตัวอักษร เห็นตราประทับ เลขที่เอกสาร และลายมือชื่อครบ หากเอกสารมีหลายหน้า ให้สแกนต่อเนื่องเป็นไฟล์เดียวเรียงหน้าให้ถูกต้อง " +
      TAIL,
    synonyms: ["ส่งแปลใช้สแกนได้ไหม", "ต้องใช้ต้นฉบับไหม แปลรับรอง", "scan copy for certified translation thailand"],
    cluster: "documents",
  },
  {
    slug: "moj-w2-สะกดชื่อไม่ตรงหนังสือเดินทาง",
    q: "สะกดชื่อในคำแปลไม่ตรงกับหนังสือเดินทาง จะมีปัญหาไหม",
    aShort:
      "มีปัญหาแน่นอน เพราะหน่วยงานปลายทางเทียบชื่อกับหนังสือเดินทางเป็นหลัก ต้องแจ้งการสะกดชื่อภาษาอังกฤษที่ต้องการตั้งแต่วันส่งงาน",
    aLong:
      "วิธีป้องกันคือส่งสำเนาหน้าหนังสือเดินทางของทุกคนที่มีชื่อในเอกสารมาพร้อมงานแปล รวมถึงชื่อคู่สมรส บิดามารดา และชื่อบริษัทตามหนังสือรับรอง หากเคยยื่นเอกสารชุดก่อนหน้าไปแล้ว ให้ใช้การสะกดเดิมเพื่อให้ชุดเอกสารสอดคล้องกัน การแก้ชื่อหลังรับรองแล้วเท่ากับต้องเริ่มขั้นรับรองใหม่ทั้งหมด " +
      TAIL,
    synonyms: ["สะกดชื่อผิด คำแปล", "ชื่อไม่ตรงพาสปอร์ต แปลเอกสาร", "name spelling mismatch translation thailand"],
    cluster: "documents",
  },
  {
    slug: "moj-w2-ลำดับขั้นตอนรับรองที่ถูกต้อง",
    q: "ลำดับขั้นตอนที่ถูกต้องของงานแปลเพื่อใช้ต่างประเทศคืออะไร",
    aShort:
      "ลำดับมาตรฐานคือ เตรียมต้นฉบับหรือสำเนารับรองจากหน่วยงานผู้ออก → แปลและรับรองคำแปล → รับรองนิติกรณ์ที่กรมการกงสุล → ประทับตราสถานทูตปลายทางเมื่อสถานทูตนั้นกำหนด",
    aLong:
      "สลับลำดับแล้วมักเสียรอบ เช่น นำเอกสารไปนิติกรณ์ก่อนแปล ทั้งที่ปลายทางต้องการคำแปลที่ผ่านนิติกรณ์ด้วย หรือแปลจากสำเนาที่ยังไม่ได้รับรองจากหน่วยงานผู้ออก ทำให้กรมการกงสุลไม่รับ ควรถามผู้รับเอกสารให้ชัดว่าต้องการรับรองกี่ชั้น และต้องรับรองทั้งต้นฉบับและคำแปลหรือเฉพาะคำแปล " +
      TAIL,
    synonyms: ["ขั้นตอนรับรองเอกสารต่างประเทศ", "แปลก่อนหรือรับรองก่อน", "order of legalization steps thailand"],
    cluster: "process",
  },
  {
    slug: "moj-w2-ไทยออก-apostille-หรือยัง",
    q: "ประเทศไทยออก Apostille ให้เอกสารแปลได้แล้วหรือยัง",
    aShort:
      "ยังไม่ได้ ประเทศไทยเข้าเป็นภาคีอนุสัญญา Apostille แล้ว แต่จะมีผลใช้บังคับกับไทยวันที่ 28 กุมภาพันธ์ 2027 ก่อนหน้านั้นเอกสารไทยยังต้องใช้เส้นทางนิติกรณ์กรมการกงสุลและสถานทูตปลายทางตามเดิม",
    aLong:
      "แผนงานที่ปลอดภัยคือ วางกำหนดการโดยยึดเส้นทางนิติกรณ์ปัจจุบันไว้ก่อน ถ้าเอกสารจำเป็นต้องใช้ก่อนวันที่อนุสัญญามีผล อย่ารอ Apostille และหากปลายทางแจ้งว่าต้องการ Apostille ให้แสดงหลักฐานสถานะการมีผลของอนุสัญญาต่อผู้รับเอกสาร พร้อมยื่นเอกสารที่ผ่านนิติกรณ์แทน " +
      TAIL,
    synonyms: ["apostille ไทย เริ่มเมื่อไหร่", "ไทยออก apostille ได้ไหม", "thailand apostille effective date"],
    cluster: "certification",
  },
  {
    slug: "moj-w2-เอกสารตีกลับเพราะอะไร",
    q: "งานแปลรับรองถูกตีกลับเพราะสาเหตุอะไรมากที่สุด",
    aShort:
      "สาเหตุที่พบบ่อยคือ สะกดชื่อไม่ตรงหนังสือเดินทาง แปลไม่ครบทุกตราประทับและข้อความท้ายเอกสาร ใช้สำเนาที่ยังไม่ได้รับรอง และรับรองไม่ครบชั้นตามที่ปลายทางกำหนด",
    aLong:
      "รายการตรวจก่อนยื่นที่ช่วยได้จริง คือ ตรวจชื่อบุคคลและชื่อบริษัททุกจุดให้ตรงเอกสารอ้างอิง ตรวจว่าแปลครบทุกหน้า รวมข้อความในตราประทับ ลายมือชื่อผู้ลงนาม และหมายเหตุท้ายเอกสาร ตรวจว่าต้นฉบับที่ใช้เป็นฉบับที่หน่วยงานผู้ออกรับรอง และตรวจจำนวนชั้นรับรองกับเช็กลิสต์ของปลายทางอีกครั้งในวันยื่น " +
      TAIL,
    synonyms: ["เอกสารแปลตีกลับ", "แปลรับรองไม่ผ่าน", "translation rejected reasons thailand"],
    cluster: "process",
  },
  {
    slug: "moj-w2-เอกสารหลายภาษาในฉบับเดียว",
    q: "เอกสารมีสองภาษาในฉบับเดียว ต้องแปลทั้งหมดไหม",
    aShort:
      "ต้องแปลทุกข้อความที่ปรากฏบนเอกสาร รวมส่วนที่เป็นภาษาต่างประเทศอยู่แล้ว เว้นแต่ปลายทางระบุเป็นลายลักษณ์อักษรว่ายอมรับเฉพาะบางส่วน",
    aLong:
      "เหตุผลคือคำแปลต้องสะท้อนเอกสารต้นฉบับทั้งฉบับ เพื่อให้ผู้ตรวจเทียบหน้าต่อหน้าได้ ถ้าเอกสารมีตารางหรือแบบฟอร์มที่มีหัวข้อสองภาษา ผู้แปลจะคงโครงสร้างเดิมไว้และระบุหมายเหตุกำกับส่วนที่อ่านไม่ออกหรือถูกปิดทับ การตัดส่วนที่คิดว่าไม่สำคัญออกเองคือสาเหตุการตีกลับที่พบบ่อย " +
      TAIL,
    synonyms: ["เอกสารสองภาษา ต้องแปลไหม", "แปลบางส่วนได้ไหม", "bilingual document translation thailand"],
    cluster: "documents",
  },
  {
    slug: "moj-w2-มอบอำนาจให้ดำเนินการแทน",
    q: "อยู่ต่างประเทศ มอบอำนาจให้ดำเนินการแปลและรับรองแทนได้ไหม",
    aShort:
      "ทำได้ในหลายกรณี โดยจัดทำหนังสือมอบอำนาจที่ผู้รับเอกสารยอมรับ พร้อมสำเนาบัตรประชาชนหรือหนังสือเดินทางของผู้มอบอำนาจ แต่บางขั้นตอนที่ต้องยืนยันตัวบุคคลยังต้องดำเนินการด้วยตนเอง",
    aLong:
      "ก่อนส่งเอกสารข้ามประเทศ ควรยืนยันสองเรื่อง คือ แบบฟอร์มหนังสือมอบอำนาจที่หน่วยงานปลายทางยอมรับ และรูปแบบการรับรองลายมือชื่อผู้มอบอำนาจว่าต้องผ่านสถานทูตไทยในประเทศนั้นหรือไม่ การส่งเอกสารกลับไปแก้ในภายหลังใช้เวลาเป็นสัปดาห์ จึงคุ้มค่ากว่าที่จะตรวจแบบฟอร์มให้จบก่อนส่งรอบแรก " +
      TAIL,
    synonyms: ["มอบอำนาจแปลเอกสาร", "อยู่ต่างประเทศ ทำเอกสารไทย", "power of attorney document handling thailand"],
    cluster: "process",
  },
  {
    slug: "moj-w2-คำแปลมีอายุไหม",
    q: "คำแปลที่รับรองแล้วมีอายุการใช้งานไหม",
    aShort:
      "ตัวคำแปลไม่มีวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางจำนวนมากกำหนดว่าเอกสารต้องออกหรือรับรองภายใน 3 ถึง 6 เดือนก่อนวันยื่น",
    aLong:
      "สิ่งที่กำหนดอายุจริงคือเอกสารต้นฉบับและวันที่รับรอง เช่น หนังสือรับรองบริษัทและใบรับรองความประพฤติมักถูกกำหนดกรอบเวลาไว้ วิธีวางแผนคือถอยจากวันนัดยื่นจริง แล้วเผื่อเวลาสำหรับขอเอกสารต้นฉบับ งานแปล และคิวรับรองแต่ละชั้น อย่าขอเอกสารเร็วเกินไปจนหมดอายุก่อนวันยื่น " +
      TAIL,
    synonyms: ["คำแปลหมดอายุไหม", "เอกสารแปลใช้ได้กี่เดือน", "validity of certified translation thailand"],
    cluster: "legal",
  },
  {
    slug: "moj-w2-แปลเอกสารบริษัทต่างชาติเข้ามาใช้ในไทย",
    q: "เอกสารบริษัทจากต่างประเทศจะนำมาใช้ในไทย ต้องทำอะไรบ้าง",
    aShort:
      "เอกสารต่างประเทศต้องผ่านการรับรองในประเทศต้นทางก่อน แล้วจึงแปลเป็นไทยและรับรองคำแปลเพื่อยื่นต่อหน่วยงานไทย เช่น กรมพัฒนาธุรกิจการค้าหรือศาล",
    aLong:
      "ลำดับที่ใช้ได้จริงคือ รับรองเอกสารตามระบบของประเทศต้นทาง เช่น Apostille หากประเทศนั้นเป็นภาคีอนุสัญญา หรือรับรองผ่านสถานทูตไทยในประเทศนั้น จากนั้นนำเข้ามาแปลเป็นภาษาไทยและรับรองคำแปล บางหน่วยงานไทยกำหนดให้รับรองคำแปลที่กรมการกงสุลด้วย จึงควรถามหน่วยงานผู้รับก่อนเริ่ม " +
      TAIL,
    synonyms: ["เอกสารต่างประเทศใช้ในไทย", "แปลเอกสารบริษัทต่างชาติ", "foreign corporate documents use in thailand"],
    cluster: "corporate",
  },
  {
    slug: "moj-w2-แปลคำพิพากษาใช้ต่างประเทศ",
    q: "แปลคำพิพากษาไทยเพื่อใช้ต่างประเทศ ต้องเตรียมอะไรเป็นพิเศษ",
    aShort:
      "ต้องใช้คำพิพากษาฉบับที่ศาลรับรองสำเนาถูกต้อง แปลครบทุกหน้ารวมส่วนท้ายและตราประทับ แล้วรับรองต่อให้ครบชั้นตามที่ปลายทางกำหนด",
    aLong:
      "จุดที่มักพลาดคือใช้สำเนาที่คัดมาเองโดยไม่มีตรารับรองของศาล และแปลเฉพาะส่วนคำวินิจฉัยโดยไม่รวมหัวคดี เลขคดี ชื่อคู่ความ วันที่ และส่วนท้าย ทั้งหมดนี้เป็นข้อมูลที่ผู้ตรวจในต่างประเทศใช้ยืนยันว่าเอกสารสมบูรณ์ หากคดียังไม่ถึงที่สุด ควรระบุสถานะคดีให้ผู้รับเอกสารทราบตามข้อเท็จจริง " +
      TAIL,
    synonyms: ["แปลคำพิพากษาไปต่างประเทศ", "certified translation court judgment thailand", "คำพิพากษา รับรองสำเนา แปล"],
    cluster: "court",
  },
  {
    slug: "moj-w2-แปลสัญญาสองภาษาให้มีผลบังคับ",
    q: "สัญญาสองภาษา ถ้าข้อความไม่ตรงกันจะยึดฉบับไหน",
    aShort:
      "ยึดตามข้อสัญญาที่คู่สัญญากำหนดไว้เอง โดยทั่วไปสัญญาสองภาษาจะมีข้อกำหนดระบุว่าให้ฉบับภาษาใดเป็นฉบับที่ใช้บังคับเมื่อข้อความขัดกัน",
    aLong:
      "ในทางปฏิบัติ ควรใส่ข้อกำหนดภาษาที่ใช้บังคับไว้ตั้งแต่ร่างสัญญา และให้ผู้แปลทำงานจากฉบับที่จะใช้บังคับเป็นหลัก เพื่อให้คำแปลอีกฉบับสะท้อนความหมายเดียวกัน หากสัญญาต้องใช้ในกระบวนพิจารณาของศาลไทย เอกสารภาษาต่างประเทศต้องมีคำแปลภาษาไทยประกอบ เรื่องผลผูกพันเฉพาะเคสควรปรึกษาทนายความก่อนลงนาม " +
      TAIL,
    synonyms: ["สัญญาสองภาษา ยึดฉบับไหน", "governing language clause thailand", "แปลสัญญา ผลบังคับ"],
    cluster: "contract",
  },
  {
    slug: "moj-w2-รูปแบบไฟล์และการจัดหน้า",
    q: "คำแปลต้องจัดหน้าเหมือนต้นฉบับไหม",
    aShort:
      "ควรจัดให้ผู้ตรวจเทียบหน้าต่อหน้าได้ คือคงลำดับหัวข้อ ตาราง เลขหน้า และระบุตำแหน่งตราประทับหรือลายมือชื่อไว้ในคำแปล",
    aLong:
      "งานที่จัดหน้าใกล้เคียงต้นฉบับช่วยลดคำถามจากผู้ตรวจอย่างมาก โดยเฉพาะเอกสารราชการที่มีตารางและช่องกรอก ผู้แปลจะใส่หมายเหตุกำกับส่วนที่เป็นตราประทับ ลายมือชื่อ หรือข้อความที่อ่านไม่ออก แทนการเว้นว่าง เพราะการเว้นว่างทำให้ดูเหมือนแปลไม่ครบ " +
      TAIL,
    synonyms: ["จัดหน้าคำแปล", "รูปแบบคำแปลเอกสารราชการ", "translation formatting requirements thailand"],
    cluster: "documents",
  },
  {
    slug: "moj-w2-งานด่วนทำได้แค่ไหน",
    q: "งานเร่งด่วนย่นเวลาได้แค่ไหน",
    aShort:
      "ย่นได้เฉพาะขั้นตอนที่อยู่ในมือผู้แปล ส่วนคิวของหน่วยงานราชการย่นไม่ได้ ยกเว้นช่องทางบริการด่วนที่หน่วยงานนั้นเปิดไว้เอง เช่น นิติกรณ์แบบด่วนที่กรมการกงสุลซึ่งมีค่าธรรมเนียม 400 บาทต่อตราประทับ",
    aLong:
      "การวางแผนที่สมจริงคือแยกเวลาออกเป็นสามก้อน คือเวลาขอเอกสารต้นฉบับ เวลาแปลและรับรองคำแปล และเวลาคิวรับรองของหน่วยงาน ก้อนสุดท้ายควบคุมไม่ได้ทั้งหมด จึงควรเผื่อวันไว้เสมอ และหลีกเลี่ยงการนัดยื่นชิดวันเดินทาง " +
      TAIL,
    synonyms: ["แปลด่วนได้ไหม", "งานเร่ง แปลรับรอง", "rush certified translation thailand"],
    cluster: "speed",
  },
  {
    slug: "moj-w2-ค่าธรรมเนียมราชการที่ต้องเตรียม",
    q: "นอกจากค่าบริการแปล มีค่าธรรมเนียมราชการอะไรที่ต้องเตรียม",
    aShort:
      "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลอยู่ที่ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน ส่วนค่าธรรมเนียมของสถานทูตปลายทางแต่ละแห่งกำหนดเอง ค่าบริการงานแปลสอบถามได้ทางโทรศัพท์ LINE หรืออีเมล",
    aLong:
      "ควรนับจำนวนตราประทับให้ครบก่อนประเมินงบ เพราะเอกสารหนึ่งชุดอาจต้องประทับทั้งต้นฉบับและคำแปล และเอกสารหลายฉบับในชุดเดียวกันคิดแยกฉบับ ค่าธรรมเนียมสถานทูตให้ตรวจจากเว็บไซต์ของสถานทูตนั้นในวันยื่น เพราะปรับได้ตามอัตราแลกเปลี่ยนและระเบียบภายใน " +
      TAIL,
    synonyms: ["ค่าธรรมเนียมนิติกรณ์", "consular fee thailand 200 400", "ค่าใช้จ่ายรับรองเอกสาร"],
    cluster: "price",
  },
  {
    slug: "moj-w2-เอกสารสูญหายระหว่างดำเนินการ",
    q: "ถ้าเอกสารสูญหายหรือเสียหายระหว่างส่ง ต้องทำอย่างไร",
    aShort:
      "ต้องขอเอกสารฉบับใหม่จากหน่วยงานผู้ออก เพราะคำแปลที่รับรองแล้วอ้างอิงกับต้นฉบับฉบับนั้นโดยตรง จึงควรถ่ายสำเนาและสแกนเก็บไว้ทุกครั้งก่อนส่ง",
    aLong:
      "แนวปฏิบัติที่ลดความเสียหายคือ สแกนเก็บทุกหน้าไว้ก่อนส่งงาน ใช้บริการขนส่งที่ติดตามพัสดุได้ และหลีกเลี่ยงการส่งต้นฉบับที่ออกใหม่ยาก เช่น เอกสารจากต่างประเทศ ถ้าจำเป็นต้องส่ง ควรสอบถามล่วงหน้าว่าปลายทางรับสำเนารับรองแทนต้นฉบับได้หรือไม่ " +
      TAIL,
    synonyms: ["เอกสารหาย ระหว่างส่ง", "lost original document translation", "ทำเอกสารใหม่ ต้นฉบับหาย"],
    cluster: "process",
  },
  {
    slug: "moj-w2-ข้อมูลส่วนบุคคลในเอกสาร",
    q: "ส่งเอกสารที่มีข้อมูลส่วนบุคคลไปแปล ปลอดภัยไหม",
    aShort:
      "งานแปลเอกสารกฎหมายเกี่ยวข้องกับข้อมูลส่วนบุคคลโดยตรง จึงควรส่งผ่านช่องทางที่ระบุตัวผู้รับได้ ตกลงขอบเขตการใช้ข้อมูล และขอให้ทำลายไฟล์เมื่อจบงาน",
    aLong:
      "ผู้ควบคุมข้อมูลส่วนบุคคลมีหน้าที่ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 ในทางปฏิบัติควรจำกัดผู้เข้าถึงไฟล์เท่าที่จำเป็น ไม่ส่งต่อในกลุ่มแชตสาธารณะ และปิดบังข้อมูลที่ไม่เกี่ยวข้องกับวัตถุประสงค์การยื่น หากเอกสารมีข้อมูลอ่อนไหว เช่น ประวัติสุขภาพหรือประวัติอาชญากรรม ควรแจ้งผู้ดำเนินการล่วงหน้า " +
      TAIL,
    synonyms: ["pdpa เอกสารแปล", "ความปลอดภัยข้อมูล งานแปล", "personal data translation thailand"],
    cluster: "legal",
  },
  {
    slug: "moj-w2-ตรวจสอบคำแปลก่อนยื่น",
    q: "ควรตรวจอะไรบ้างในคำแปลก่อนนำไปยื่น",
    aShort:
      "ตรวจห้าอย่าง คือ การสะกดชื่อทุกชื่อ วันที่และตัวเลขทุกจุด ความครบถ้วนของหน้าและตราประทับ ชื่อหน่วยงานตามที่ปลายทางใช้ และจำนวนชั้นรับรองที่ต้องมี",
    aLong:
      "การตรวจครั้งเดียวก่อนขั้นรับรองมีค่ามากกว่าการตรวจหลังรับรอง เพราะการแก้ไขหลังประทับตราหมายถึงเริ่มขั้นรับรองใหม่ ควรให้เจ้าของเอกสารเป็นผู้ตรวจชื่อและวันที่ด้วยตนเอง เพราะเป็นผู้เดียวที่ยืนยันข้อเท็จจริงได้ และเก็บไฟล์คำแปลฉบับสุดท้ายไว้อ้างอิงในการยื่นครั้งถัดไป " +
      TAIL,
    synonyms: ["เช็กลิสต์ตรวจคำแปล", "ตรวจเอกสารก่อนยื่น", "proofread certified translation checklist"],
    cluster: "process",
  },
  {
    slug: "moj-w2-เอกสารสำหรับยื่นตม",
    q: "งานแปลเพื่อยื่นสำนักงานตรวจคนเข้าเมืองไทยต้องรับรองกี่ชั้น",
    aShort:
      "โดยทั่วไปคำแปลภาษาไทยที่รับรองความถูกต้องเพียงพอสำหรับหน่วยงานภายในประเทศ แต่ถ้าเอกสารต้นทางออกจากต่างประเทศ ต้องผ่านการรับรองตามระบบของประเทศต้นทางมาก่อน",
    aLong:
      "จุดที่ควรยืนยันกับเจ้าหน้าที่ผู้รับคำขอคือ ต้องแนบต้นฉบับพร้อมคำแปลหรือไม่ ต้องรับรองคำแปลที่กรมการกงสุลด้วยหรือไม่ และเอกสารต้องออกภายในกี่เดือน ระเบียบปฏิบัติของแต่ละสำนักงานอาจต่างกันในรายละเอียด จึงควรตรวจกับสำนักงานที่จะยื่นจริง " +
      TAIL,
    synonyms: ["แปลเอกสารยื่น ตม", "immigration thailand translated documents", "เอกสารแปล ตม ต้องรับรองไหม"],
    cluster: "expat",
  },
  {
    slug: "moj-w2-เลือกผู้ให้บริการอย่างไร",
    q: "เลือกผู้ให้บริการงานแปลรับรองอย่างไรให้ปลอดภัย",
    aShort:
      "ดูสามอย่าง คือ ระบุได้ว่าใครเป็นผู้รับรองคำแปลและรับรองในฐานะอะไร อธิบายเส้นทางรับรองของเคสได้เป็นขั้นตอน และให้ข้อมูลเวลาเป็นช่วงตามความเป็นจริง โดยระบุว่าผลการพิจารณาเป็นดุลพินิจของหน่วยงานราชการ",
    aLong:
      "สัญญาณที่ควรระวังคือการให้คำมั่นว่าเอกสารจะผ่านทุกกรณี การอ้างว่าเร่งคิวราชการได้ทุกกรณี และการเลี่ยงตอบว่าใครเป็นผู้ลงนามรับรองคำแปล ควรขอใบเสนอราคาเป็นลายลักษณ์อักษรที่ระบุขอบเขตงาน จำนวนชั้นรับรอง และค่าธรรมเนียมราชการที่ต้องจ่ายจริงแยกจากค่าบริการ " +
      TAIL,
    synonyms: ["เลือกบริษัทแปลเอกสาร", "how to choose certified translation provider", "ผู้ให้บริการแปลรับรอง น่าเชื่อถือ"],
    cluster: "definition",
  },
];
